<template>
  <div class="otp-container">
    <div class="otp-content">
      <h1 class="otp-title">短信验证码</h1>
      <p class="otp-hint">验证码已发送至手机 {{ maskedPhone }}，请输入6位短信验证码。<strong>如果已更换手机号，请联系平台管理员。</strong></p>

      <!-- 3分钟倒计时 -->
      <div class="sms-countdown" v-if="expireCountdown > 0">
        <el-text type="info">验证码有效期 {{ formatTime(expireCountdown) }}</el-text>
      </div>
      <div class="sms-countdown expired" v-else>
        <el-text type="danger">验证码已过期，请重新发送</el-text>
      </div>

      <div class="otp-inputs">
        <input
          v-for="(digit, index) in otpDigits"
          :key="index"
          :ref="'otpInput' + index"
          :value="otpDigits[index]"
          type="text"
          maxlength="1"
          class="otp-input-native"
          @input="handleInput(index, $event)"
          @keydown="handleKeydown(index, $event)"
          @paste="handlePaste"
        />
      </div>

      <div class="otp-message">
        <el-text v-if="errorMessage" type="danger" style="color:red">{{ errorMessage }}</el-text>
        <el-text v-else-if="successMessage" type="success">{{ successMessage }}</el-text>
      </div>

      <el-button
        type="primary"
        class="otp-submit-button"
        :disabled="!isOTPComplete || loading || expireCountdown <= 0"
        :loading="loading"
        @click="verifySMS"
      >
        验证
      </el-button>

      <!-- Resend code 按钮 -->
      <div class="resend-area">
        <el-button
          type="text"
          :disabled="resendCountdown > 0 || sendingCode"
          :loading="sendingCode"
          @click="resendCode"
        >
          <span v-if="resendCountdown > 0">重新发送验证码 ({{ resendCountdown }}s)</span>
          <span v-else>重新发送验证码</span>
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SmsVerify',
  props: {
    phone: {
      type: String,
      default: ''
    },
    userInfo: {
      type: Object,
      default: null
    },
    smsSendCodeUrl: {
      type: String,
      default: 'coupon-system/sms/sendCodeByPhone'
    },
    smsVerifyCodeUrl: {
      type: String,
      default: 'coupon-system/sms/verifyCodeByPhone'
    },
    autoSend: {
      type: Boolean,
      default: false
    },
    showBackBtn: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      otpDigits: ['', '', '', '', '', ''],
      loading: false,
      errorMessage: '',
      successMessage: '',
      sendingCode: false,
      expireCountdown: 0,
      expireEndTime: 0, // 过期倒计时结束时间戳
      expireTimer: null,
      resendCountdown: 0,
      resendEndTime: 0, // 重发倒计时结束时间戳
      resendTimer: null
    }
  },
  computed: {
    isOTPComplete() {
      return this.otpDigits.every(digit => digit.length === 1)
    },
    maskedPhone() {
      if (!this.phone || this.phone.length < 7) return this.phone
      return this.phone.substring(0, 3) + '****' + this.phone.substring(this.phone.length - 4)
    }
  },
  methods: {
    formatTime(seconds) {
      const min = Math.floor(seconds / 60)
      const sec = seconds % 60
      return min + ':' + (sec < 10 ? '0' : '') + sec
    },
    handleInput(index, event) {
      let value = event.target.value
      if (value && !/^\d*$/.test(value)) {
        this.otpDigits.splice(index, 1, '')
        return
      }
      this.otpDigits.splice(index, 1, value)
      this.errorMessage = ''
      this.successMessage = ''
      if (value && index < 5) {
        this.$nextTick(() => {
          const nextInput = this.getInputRef(index + 1)
          if (nextInput) nextInput.focus()
        })
      }
    },
    handleKeydown(index, event) {
      if (event.key === 'Enter' && this.isOTPComplete) {
        event.preventDefault()
        this.verifySMS()
        return
      }
      if (event.key === 'Backspace') {
        if (this.otpDigits[index]) {
          this.otpDigits.splice(index, 1, '')
        } else if (index > 0) {
          this.otpDigits.splice(index - 1, 1, '')
          this.$nextTick(() => {
            const prevInput = this.getInputRef(index - 1)
            if (prevInput) prevInput.focus()
          })
        }
        event.preventDefault()
      }
      if (event.key === 'ArrowLeft' && index > 0) {
        const prevInput = this.getInputRef(index - 1)
        if (prevInput) prevInput.focus()
      }
      if (event.key === 'ArrowRight' && index < 5) {
        const nextInput = this.getInputRef(index + 1)
        if (nextInput) nextInput.focus()
      }
    },
    handlePaste(event) {
      event.preventDefault()
      const pastedText = event.clipboardData ? event.clipboardData.getData('text') : ''
      const digits = pastedText.replace(/\D/g, '').slice(0, 6)
      for (let i = 0; i < digits.length; i++) {
        this.otpDigits[i] = digits[i]
      }
      const nextEmptyIndex = this.otpDigits.findIndex(d => !d)
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : Math.min(digits.length, 5)
      this.$nextTick(() => {
        const focusInput = this.getInputRef(focusIndex)
        if (focusInput) focusInput.focus()
      })
    },
    getInputRef(index) {
      const ref = this.$refs['otpInput' + index]
      if (ref && ref[0]) return ref[0]
      return ref
    },
    sendSmsCode() {
      const _this = this
      _this.sendingCode = true
      _this.errorMessage = ''
      _this.$ajax({
        url: _this.smsSendCodeUrl + '?phoneNum=' + _this.phone,
        methods: 'POST',
        token: window.sessionStorage.getItem('token'),
        success: function(response) {
          _this.sendingCode = false
          if (response.code === 0) {
            _this.successMessage = '验证码已发送'
            _this.startExpireCountdown()
            _this.startResendCountdown()
          } else {
            _this.errorMessage = response.message || '发送验证码失败'
          }
        },
        error: function() {
          _this.sendingCode = false
          _this.errorMessage = '发送验证码失败'
        }
      })
    },
    resendCode() {
      this.otpDigits = ['', '', '', '', '', '']
      this.errorMessage = ''
      this.successMessage = ''
      this.sendSmsCode()
      this.$nextTick(() => {
        const firstInput = this.getInputRef(0)
        if (firstInput) firstInput.focus()
      })
    },
    verifySMS() {
      if (!this.isOTPComplete || this.expireCountdown <= 0) return
      this.loading = true
      this.errorMessage = ''
      this.successMessage = ''

      const smsCode = this.otpDigits.join('')
      const _this = this
      this.$ajax({
        url: _this.smsVerifyCodeUrl + '?phoneNum=' + _this.phone + '&code=' + smsCode,
        methods: 'POST',
        token: window.sessionStorage.getItem('token'),
        success: function(response) {
          _this.loading = false
          if (response.code === 0) {
            _this.successMessage = '验证通过'
            _this.$emit('verify-success', smsCode, _this.userInfo)
          } else {
            _this.errorMessage = response.message || '验证失败，请重新输入'
          }
        },
        error: function() {
          _this.loading = false
          _this.errorMessage = '验证失败，请重新输入'
        }
      })
    },
    startExpireCountdown() {
      if (this.expireTimer) clearInterval(this.expireTimer)
      this.expireEndTime = Date.now() + 180 * 1000
      this.expireCountdown = 180
      this.expireTimer = setInterval(() => {
        const remaining = Math.round((this.expireEndTime - Date.now()) / 1000)
        if (remaining <= 0) {
          this.expireCountdown = 0
          clearInterval(this.expireTimer)
          this.expireTimer = null
        } else {
          this.expireCountdown = remaining
        }
      }, 1000)
    },
    startResendCountdown() {
      if (this.resendTimer) clearInterval(this.resendTimer)
      this.resendEndTime = Date.now() + 60 * 1000
      this.resendCountdown = 60
      this.resendTimer = setInterval(() => {
        const remaining = Math.round((this.resendEndTime - Date.now()) / 1000)
        if (remaining <= 0) {
          this.resendCountdown = 0
          clearInterval(this.resendTimer)
          this.resendTimer = null
        } else {
          this.resendCountdown = remaining
        }
      }, 1000)
    },
    focusInput() {
      this.$nextTick(() => {
        const firstInput = this.getInputRef(0)
        if (firstInput) firstInput.focus()
      })
    }
  },
  mounted() {
    this.focusInput()
    if (this.autoSend) {
      this.sendSmsCode()
    }
  },
  beforeDestroy() {
    if (this.expireTimer) clearInterval(this.expireTimer)
    if (this.resendTimer) clearInterval(this.resendTimer)
  }
}
</script>

<style scoped>
.otp-container {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
}

.otp-content {
  position: relative;
  width: 100%;
  max-width: 480px;
  text-align: center;
  max-height: 100vh;
  overflow-y: auto;
  padding: 10px 0;
}

.otp-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #303133;
}

.otp-hint {
  font-size: 14px;
  color: #606266;
  margin: 0 0 16px 0;
  line-height: 1.6;
}

.sms-countdown {
  margin-bottom: 16px;
}

.sms-countdown.expired {
  margin-bottom: 16px;
}

.otp-inputs {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 16px;
}

.otp-input-native {
  width: 40px;
  height: 40px;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s;
}

.otp-input-native:focus {
  border-color: #409EFF;
}

.otp-message {
  min-height: 24px;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.otp-submit-button {
  width: 100%;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
}

.resend-area {
  margin-bottom: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/smsVerify.vue